import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useFacilitatorPortalService,
  FacilitatorSessionView,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { buildSessionTabs, SessionTabEntry } from '../Tabs/buildSessionTabs';

export interface UseSessionTabsResult {
  tabs: SessionTabEntry[];
  loading: boolean;
  loaded: boolean;
  refetch: () => Promise<void>;
  /** Change a session's status via the facilitator portal. */
  changeSessionStatus: (registrationSessionId: number, newStatus: number) => Promise<void>;
  changingStatus: boolean;
  /** Change a session's progress status via the facilitator portal. */
  changeSessionProgress: (registrationSessionId: number, newProgressStatus: number) => Promise<void>;
  changingProgress: boolean;
}

/**
 * Fetches the facilitator's sessions for a given registration via
 * `facilitatorPortalService.mySessions()` and derives the dynamic tab
 * list so the CALLING page can render them as top-level tabs.
 *
 * `mySessions()` returns FacilitatorSessionView, which carries
 * `orderNumber` directly — no separate course-session catalogue lookup
 * is needed to sort tabs, unlike the old RegistrationSessionModelView
 * based approach.
 *
 * ⚠️ Scoped **server-side** via `mySessions(registrationId)` (SDK 0.0.50). It used to fetch every
 * session the facilitator had and filter by `registrationID` in memory; that was correct but pulled
 * the whole set on every registration open. Do not reintroduce the client-side filter.
 */
export function useSessionTabs(
  registrationId: number,
  active: boolean
): UseSessionTabsResult {
  const facilitatorPortalService = useFacilitatorPortalService();

  const [allSessions, setAllSessions] = useState<FacilitatorSessionView[] | null>(null);
  const [loading, setLoading] = useState(false);

  const [changingStatus, setChangingStatus] = useState(false);
  const [changingProgress, setChangingProgress] = useState(false);

  const fetchSessions = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    try {
      const res = await facilitatorPortalService.mySessions(registrationId);
      setAllSessions(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error('Error fetching facilitator sessions:', e);
      toast.error('Error', 'Could not load registration sessions.');
      setAllSessions([]);
    } finally {
      setLoading(false);
    }
  }, [registrationId, facilitatorPortalService]);

  useEffect(() => {
    if (active && allSessions === null) fetchSessions();
  }, [active, allSessions, fetchSessions]);

  // Already scoped to this registration by the server.
  const sessions = useMemo(() => allSessions ?? [], [allSessions]);

  const tabs = useMemo(() => buildSessionTabs(sessions), [sessions]);

  const changeSessionStatus = useCallback(
    async (registrationSessionId: number, newStatus: number) => {
      setChangingStatus(true);
      try {
        const updated = await facilitatorPortalService.changeSessionStatus(registrationSessionId, newStatus);
        setAllSessions((prev) =>
          prev
            ? prev.map((s) =>
                s.registrationSessionID === registrationSessionId ? { ...s, ...updated } : s
              )
            : prev
        );
      } catch (e) {
        console.error('Error changing session status:', e);
        toast.error('Error', 'Could not update session status.');
      } finally {
        setChangingStatus(false);
      }
    },
    [facilitatorPortalService]
  );

  const changeSessionProgress = useCallback(
    async (registrationSessionId: number, newProgressStatus: number) => {
      setChangingProgress(true);
      try {
        const updated = await facilitatorPortalService.changeSessionProgress(
          registrationSessionId,
          newProgressStatus
        );
        setAllSessions((prev) =>
          prev
            ? prev.map((s) =>
                s.registrationSessionID === registrationSessionId ? { ...s, ...updated } : s
              )
            : prev
        );
      } catch (e) {
        console.error('Error changing session progress:', e);
        toast.error('Error', 'Could not update session progress.');
      } finally {
        setChangingProgress(false);
      }
    },
    [facilitatorPortalService]
  );

  return {
    tabs,
    loading,
    loaded: allSessions !== null,
    refetch: fetchSessions,
    changeSessionStatus,
    changingStatus,
    changeSessionProgress,
    changingProgress,
  };
}